import _ from 'lodash';
import React from 'react';
import { Row, Col, Card, Table, Button, ButtonGroup, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import moment from 'moment';

import browserHistory from '../../history';
import AuthService from '../../services/AuthService';
import FileService from '../../services/FileService';
import { getStatus } from './PrintJobUtil';
import { extractTimestamp } from '../../util/general';
import { modal } from '../modal/ModalEmitter';
import PdfObject from '../PdfObject';
import { AxiosResponse } from 'axios';

interface Props {
  enableAdminControls: boolean;
  handleDownloadPdf: () => void;
  handleGetInfo: (output: string) => Promise<AxiosResponse>;
  handleLayerChange: () => void;
  handleResubmitJob: () => void;
  handleRetryJob: () => void;
  id: string;
  layer: string;
  printJob: any;
}

const PrintJob = ({
  enableAdminControls,
  handleDownloadPdf,
  handleGetInfo,
  handleLayerChange,
  handleResubmitJob,
  handleRetryJob,
  id,
  layer,
  printJob,
}: Props) => {
  const isSecure = () => printJob.payload.secure;

  const renderInfo = (output: string) => {
    handleGetInfo(output).then(({ data }: AxiosResponse) => {
      let message;
      try {
        message = <pre>{JSON.stringify(JSON.parse(data.payload), null, 2)}</pre>;
      } catch (err: any) {
        message = (
          <div>
            {err.message}
            <pre>{data.payload}</pre>
          </div>
        );
      }
      modal.info({ title: `${output} info`, message });
    });
  };

  const renderToolbar = () => {
    if (!enableAdminControls) {
      return null;
    }

    return (
      <ButtonGroup size="sm">
        {(!isSecure() || AuthService.isDsiSupport()) && (
          <Button onClick={() => browserHistory.push(`/UPD1/${id}/edit`)}>Edit</Button>
        )}
        {!isSecure() && (
          <Button onClick={handleResubmitJob} variant="secondary">
            Resubmit
          </Button>
        )}
        <Button onClick={handleRetryJob} variant="secondary">
          Retry Failed
        </Button>
      </ButtonGroup>
    );
  };

  const renderNotes = () => {
    const { notes } = printJob;

    if (!notes || !notes.length) {
      return null;
    }

    // localize time stamp in notes
    const parsedNotes = notes.map((line: string, i: number) => {
      const [ts, status, body] = line.split(' - ');

      return (
        <tr key={String(i)}>
          <td>{moment(ts).format('MMMM Do YYYY, h:mm:ss a')}</td>
          <td>{status}</td>
          <td>{body}</td>
        </tr>
      );
    });

    return (
      <Card>
        <Card.Header>
          <Card.Title>Notes</Card.Title>
        </Card.Header>
        <Table striped bordered>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Status</th>
              <th>Body</th>
            </tr>
          </thead>
          <tbody>{parsedNotes}</tbody>
        </Table>
      </Card>
    );
  };

  const renderPrintJob = () => {
    if (!printJob.id) {
      return null;
    }

    return (
      <Card className="mb-3">
        <Card.Header>
          <Card.Title>
            Print Job
            <div className="float-right">{renderToolbar()}</div>
          </Card.Title>
        </Card.Header>
        <Table striped bordered>
          <thead>
            <tr>
              <th>Attribute</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>ID</td>
              <td>{printJob.id}</td>
            </tr>
            <tr>
              <td>Description</td>
              <td>{printJob.description}</td>
            </tr>
            <tr>
              <td>Template</td>
              <td>
                <a
                  onClick={e => {
                    e.preventDefault();
                    browserHistory.push(`/db/UPT1/${printJob.templateId}/edit`);
                  }}
                  href={`/db/UPT1/${printJob.templateId}/edit`}
                >
                  {_.get(printJob, 'template.name')}
                </a>
              </td>
            </tr>
            <tr>
              <td>Created At</td>
              <td>{moment(extractTimestamp(printJob.id)).format('MMMM Do YYYY, h:mm:ss a')}</td>
            </tr>
            <tr>
              <td>Status</td>
              <td>{(getStatus(printJob.overallStatus) || {}).label}</td>
            </tr>
          </tbody>
        </Table>
      </Card>
    );
  };

  const renderOutputValue = (output: string) => {
    if (!printJob.payload || !printJob.payload[output]) {
      return null;
    }

    const data = printJob.payload[output] || [];

    switch (output) {
      case 'print':
        return data.map((x: any, i: number) => (
          <div key={String(i)}>
            {x.name} ({x.copies} copies)
          </div>
        ));

      case 'email':
        return data.map((x: any, i: number) => <div key={String(i)}>{x}</div>);

      case 'archive':
        return <div>{data}</div>;

      case 'ftp':
        return data.map((x: any, i: number) => (
          <div key={String(i)}>
            {x.type}: {x.path}
          </div>
        ));

      default:
        console.warn('Invalid Output');
        return null;
    }
  };

  const renderOutputs = () => {
    let hasOutput = false;

    const outputs = ['print', 'email', 'archive', 'ftp', 'screen'].map((output: string, i: number) => {
      const outputStatus = getStatus(printJob[`${output}Status`]);

      if (!outputStatus) {
        return null;
      }

      hasOutput = true;

      return (
        <tr key={String(i)}>
          <td>{output}</td>
          <td>
            {outputStatus.label}
            {!!outputStatus.ts && ` - ${moment(outputStatus.ts * 1000).format('MMMM Do YYYY, h:mm:ss a')}`}
          </td>
          <td>{renderOutputValue(output)}</td>
          <td>
            {output !== 'screen' && (
              <Button size="sm" onClick={() => renderInfo(output)} variant="secondary">
                Info
              </Button>
            )}
          </td>
        </tr>
      );
    });

    if (!hasOutput) {
      return null;
    }

    return (
      <Card className="mb-3">
        <Card.Header>
          <Card.Title>Outputs</Card.Title>
        </Card.Header>
        <Table striped bordered>
          <tbody>{outputs}</tbody>
        </Table>
      </Card>
    );
  };

  const renderPdf = () => {
    const pdfUrl = FileService.downloadPath(`${printJob.id}-${layer}.pdf`);

    if (!printJob.pdfExists) {
      return <p>PDF not ready</p>;
    }

    return <PdfObject url={pdfUrl} width="100%" height="800" />;
  };

  const renderPdfPreview = () => {
    if (!printJob) {
      return <div>PrintJob not found.</div>;
    }

    if (isSecure() && !AuthService.isDsiSupport()) {
      return <div>Cannot Access Preview</div>;
    }

    if (!printJob.template) {
      return <div>Invalid Template</div>;
    }

    const { overlay, underlay } = printJob.template;

    return (
      <Card>
        <Card.Header>
          <Card.Title>
            PDF Preview
            <div className="float-right">
              {(underlay || overlay) && (
                <ToggleButtonGroup
                  size="sm"
                  type="radio"
                  name="layer"
                  value={layer}
                  onChange={handleLayerChange}
                  defaultValue={layer}
                  style={{ marginRight: '5px' }}
                >
                  <ToggleButton value="base">Base</ToggleButton>
                  {!!overlay && <ToggleButton value="over">Overlay</ToggleButton>}
                  {!!underlay && <ToggleButton value="under">Underlay</ToggleButton>}
                  {underlay && overlay && <ToggleButton value="both">Both</ToggleButton>}
                </ToggleButtonGroup>
              )}
              <Button size="sm" onClick={handleDownloadPdf} variant="secondary">
                Download
              </Button>
            </div>
          </Card.Title>
        </Card.Header>
        <Card.Body className="text-center" style={{ padding: 0 }}>
          {renderPdf()}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="content-container">
      <Row>
        <Col xs={12} sm={6}>
          {renderPrintJob()}
          {renderNotes()}
          {renderOutputs()}
        </Col>
        <Col xs={12} sm={6}>
          {renderPdfPreview()}
        </Col>
      </Row>
    </div>
  );
};

export default PrintJob;
